import styles from './RentalExtendModal.module.css';

import type { FC } from 'react';
import type { DealChain } from 'src/utils/dealChain';
import type { ApiRentalDealItem } from 'src/types/rental';

import React, { useState } from 'react';
import { Warning, CheckCircle, CalendarPlus } from '@phosphor-icons/react';

import { formatDateTime } from 'src/utils/date';
import { extensionBlockedNote } from 'src/utils/rentalOccupancy';
import { readRentalPaymentToken } from 'src/utils/rentalOnchain';
import { rentalToken, platformFeeOf, formatBaseUnits } from 'src/utils/rentalInvoice';

import { useWalletUI, WalletButton } from 'src/blockchain';
import { useRentalExtensionQuote } from 'src/queries/useRentalClockQuotes';

import { Modal } from 'src/components/Modal';
import { Stack } from 'src/components/themed/Stack';
import { Typography } from 'src/components/themed/Typography';
import { TxProgress, useTxProgress, useClearWhenWalletConnects } from 'src/components/TxProgress';

interface RentalExtendModalProps {
  visible: boolean;
  onClose: () => void;
  rental: ApiRentalDealItem;
  chain: DealChain | null;
  /** Called after the on-chain requestExtension() succeeds so the screen can refetch. */
  onRequested?: () => void;
}

const DAY_SECONDS = 86_400;

/**
 * CC4 step 1: the renter requests a rental extension. Nothing is charged now — the dealer's
 * `approveExtension()` pulls the extra rental + fee from the renter's wallet via `transferFrom`,
 * so the renter must pre-approve the exact amount here. Two wallet prompts: approve(totalExtra)
 * then requestExtension(newPeriod).
 */
export const RentalExtendModal: FC<RentalExtendModalProps> = ({ visible, onClose, rental, chain, onRequested }) => {
  const walletUI = useWalletUI(chain?.family);
  const tx = useTxProgress();

  useClearWhenWalletConnects(tx, chain?.family);

  const [days, setDays] = useState(1);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const state = rental.rentalContractState;
  const token = rentalToken(rental, chain);
  const contractAddress = state?.address ?? rental.contractAddress ?? '';

  const newPeriod = days > 0 ? BigInt(days * DAY_SECONDS) : 0n;

  // The price is the CONTRACT's, not ours: v2.2 charges the tier band this extension's own length
  // falls into (2 days on a 10-day booking cost 2 × the 1–3 day rate), which is not derivable from
  // the booking's own average. Quoting it here would eventually disagree with what
  // `approveExtension()` pulls — and the renter would have approved the wrong sum.
  //
  // Only v2.2 and newer contracts can quote. An older deal still charges what ITS contract charges
  // (rentalAmount × newPeriod / rentalPeriod), so that stays as the legacy path instead of leaving
  // such a renter with a price that never loads.
  const isQuotingContract = (state?.contractVersion ?? 1) >= 3;
  const {
    data: quote,
    isLoading,
    error: quoteError,
  } = useRentalExtensionQuote(rental.id, days, visible && days > 0 && isQuotingContract);
  const isQuoting = isQuotingContract && isLoading;

  const toBig = (value: string | undefined): bigint => {
    try {
      return value ? BigInt(value) : 0n;
    } catch {
      return 0n;
    }
  };

  const legacyExtraRental =
    !isQuotingContract && state != null && newPeriod > 0n && BigInt(state.rentalPeriod) > 0n
      ? (BigInt(state.rentalAmount) * newPeriod) / BigInt(state.rentalPeriod)
      : 0n;
  const extraRental = isQuotingContract ? toBig(quote?.extraRental) : legacyExtraRental;
  const extraFee = isQuotingContract ? toBig(quote?.extraFee) : platformFeeOf(legacyExtraRental);
  const totalExtra = isQuotingContract ? toBig(quote?.totalExtra) : extraRental + extraFee;
  const startsFrom = Number(quote?.startsFrom ?? '0');
  const format = (value: bigint) => `${formatBaseUnits(value, token.decimals)} ${token.symbol}`;
  const isProcessing = (isSubmitting || tx.isRunning) && !success;

  // Whether the car is free for those days at all (api occupancy-truth §4.5). The dealer
  // physically cannot approve an extension over someone else's booking, so the renter is told
  // here — before approving money for it — instead of paying and waiting for a decline. A
  // dealer's own block is not shown: it is his to move, and he may well approve anyway.
  const blockedNote = extensionBlockedNote(quote?.availability);

  const handleClose = () => {
    if (isProcessing) {
      return;
    }
    setError('');
    setSuccess(false);
    tx.reset();
    onClose();
  };

  const handleConfirm = async () => {
    if (isProcessing) {
      return;
    }

    setError('');
    setIsSubmitting(true);

    try {
      if (!contractAddress || state == null) {
        throw new Error('The escrow contract is not ready yet. Please try again shortly.');
      }
      if (days < 1) {
        throw new Error('Extension must be at least 1 day.');
      }
      if (totalExtra <= 0n) {
        throw new Error('Could not read the extension price from the escrow. Please try again shortly.');
      }

      const paymentToken = await readRentalPaymentToken(walletUI, contractAddress, chain?.chainId);

      const done = await tx.run(
        (onProgress) =>
          walletUI.callContract({
            contractAddress,
            signature: 'requestExtension(uint256)',
            args: [newPeriod.toString()],
            chainId: chain?.chainId,
            approve: { tokenAddress: paymentToken, spender: contractAddress, amount: totalExtra.toString() },
            onProgress,
          }),
        'Transaction failed. Please try again.'
      );

      if (done) {
        setSuccess(true);
        onRequested?.();
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Transaction failed. Please try again.';
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      visible={visible}
      onClose={handleClose}
      icon={
        success ? (
          <CheckCircle size={64} color="white" weight="fill" />
        ) : (
          <CalendarPlus size={64} color="white" weight="fill" />
        )
      }
      header={success ? 'Extension requested' : 'Extend this rental'}
      buttonText={
        success ? 'Close' : isProcessing ? 'Processing…' : isQuoting ? 'Checking the price…' : 'Approve & request'
      }
      onSubmit={success ? handleClose : handleConfirm}
      disabled={isProcessing || (!success && (days < 1 || isQuoting || totalExtra <= 0n || blockedNote !== null))}
    >
      <Stack gap={3} width="100%">
        {!success ? (
          <>
            <Stack gap={1} className={styles.info}>
              <Typography color="white" fontWeight="600">
                Extend by (days)
              </Typography>
              <input
                type="number"
                min={1}
                step={1}
                value={days}
                onChange={(e) => setDays(Math.max(0, Math.floor(Number(e.target.value))))}
                className={styles.daysInput}
                disabled={isProcessing}
              />
              <Row label="Extra rental" value={format(extraRental)} />
              <Row label="Platform fee (9%)" value={format(extraFee)} />
              <Row label="Total extra charge" value={format(totalExtra)} />
              {startsFrom > 0 ? (
                <Row label="New days start from" value={formatDateTime(startsFrom * 1000)} />
              ) : null}
            </Stack>

            {isQuotingContract ? (
              <Typography color="lightWhite" variant="small">
                Priced at your dealer&apos;s published rate for a {days}-day rental, not at the average of your original
                booking. Days you have already paid for past the end date are never sold to you twice — that is why the
                new days start where they do.
              </Typography>
            ) : null}

            {blockedNote ? (
              <Stack gap={1} className={styles.warning}>
                <Stack flexDirection="row" gap={1} alignItems="center">
                  <Warning size={20} color="white" weight="fill" />
                  <Typography color="white" fontWeight="600">
                    These dates are taken
                  </Typography>
                </Stack>
                <Typography color="lightWhite" variant="small">
                  {blockedNote} Try a shorter extension, or talk to the dealer about returning the car on time.
                </Typography>
              </Stack>
            ) : null}

            <Stack gap={1} className={styles.warning}>
              <Stack flexDirection="row" gap={1} alignItems="center">
                <Warning size={20} color="white" weight="fill" />
                <Typography color="white" fontWeight="600">
                  Nothing is charged until the dealer approves
                </Typography>
              </Stack>
              <Typography color="lightWhite" variant="small">
                Two prompts will appear: first your wallet asks you to <b>approve exactly {format(totalExtra)}</b> —
                this is the extension charge, not a gas fee, so do not lower it. Then confirm the request. The amount is
                only charged if the dealer approves the extension; keep it available in your wallet until then.
              </Typography>
            </Stack>
          </>
        ) : (
          <Stack gap={1} className={styles.info}>
            <Typography color="white" fontWeight="600">
              Extension requested. You will see the new end date here once the dealer approves.
            </Typography>
          </Stack>
        )}

        <TxProgress
          state={tx.state}
          done={success}
          error={tx.error}
          // No wallet to sign with: `Connect Wallet` and the connection window stand in for Retry (app#315).
          connect={tx.needsWallet && chain ? <WalletButton family={chain.family} /> : null}
          explorerTxUrl={chain?.explorerTxUrl}
          onRetry={() => {
            tx.reset();
            handleConfirm();
          }}
        />

        {!success && isQuotingContract && quoteError ? (
          <Typography color="white" variant="small" className={styles.error}>
            Could not read the extension price from the escrow. Please close and try again.
          </Typography>
        ) : null}

        {error ? (
          <Typography color="white" variant="small" className={styles.error}>
            {error}
          </Typography>
        ) : null}
      </Stack>
    </Modal>
  );
};

const Row: FC<{ label: string; value: string }> = ({ label, value }) => (
  <Stack flexDirection="row" justifyContent="space-between" gap={2}>
    <Typography color="lightWhite" variant="small">
      {label}
    </Typography>
    <Typography color="white" variant="small">
      {value}
    </Typography>
  </Stack>
);
